import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { HeaderNavigationComponent } from 'components/header-navigation/header-navigation.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { Subscription } from 'rxjs';
import { ApiService } from 'services/api.service';
import { AuthorizationService, USER_SECURITY_INFO_KEY } from 'services/authorization.service';
import { NotificationService } from 'services/notification.service';
import { UserNotificationListByUserIdItem, UserNotificationListByUserIdItemPagedResponse } from 'types/user.interface';
import { VersionResponse } from 'types/version.interface';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [HeaderNavigationComponent, TranslateModule, CommonModule, ConfirmModalComponent, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  isLogoutConfirmOpen: boolean = false;
  unreadNotificationCount: number = 0;
  notificationSubscription!: Subscription;
  userId?: string;
  version?: VersionResponse;
  environmentCode = '';
  constructor(
    public authService: AuthorizationService,
    public apiService: ApiService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadVersionFromLocalStorage();

    this.notificationSubscription = this.apiService.getNotificationReadObservable().subscribe(() => {
      this.loadUserNotifications(this.userId!);
    });
    const userSecurityInfo = localStorage.getItem(USER_SECURITY_INFO_KEY);
    if (userSecurityInfo) {
      try {
        const parsedInfo = JSON.parse(userSecurityInfo);
        this.userId = parsedInfo.userId;
        if (this.userId) {
          this.loadUserNotifications(this.userId);
        }
      } catch (error) {
        console.error('Error parsing user security info:', error);
      }
    }
    this.notificationSubscription = this.notificationService.notifications$.subscribe((notifications) => {
      const newCount = notifications?.list?.filter((notification: UserNotificationListByUserIdItem) => !notification.isRead).length ?? 0;
      if (this.unreadNotificationCount !== newCount) {
        this.unreadNotificationCount = newCount;
      }
    });
  }

  private loadVersionFromLocalStorage() {
    const versionStr = localStorage.getItem('versionInfo');
    if (versionStr) {
      try {
        this.version = JSON.parse(versionStr) as VersionResponse;
        this.environmentCode = this.version.environmentCode;
      } catch (err) {
        console.error('Error parsing version info from localStorage:', err);
      }
    } else {
      this.authService.getVersionInfo().subscribe({
        next: (version) => {
          this.version = version;
          localStorage.setItem('versionInfo', JSON.stringify(version));
        },
        error: (err) => console.error('Error fetching version info:', err)
      });
    }
  }

  loadUserNotifications(userId: string) {
    this.apiService.getAllUserNotifications(userId).subscribe(
      (data: UserNotificationListByUserIdItemPagedResponse) => {
        this.unreadNotificationCount = data?.list?.filter((x: UserNotificationListByUserIdItem) => !x.isRead).length || 0;
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  handleLogout(): void {
    const url = this.router.url;
    if (url.includes('/pakalpojumi/informacijas-pieprasijums') || url.includes('/pakalpojumi/lasitavas-apmeklejums')) {
      this.isLogoutConfirmOpen = true;
    } else {
      this.authService.logout();
    }
  }

  confirmLogout(): void {
    this.isLogoutConfirmOpen = false;
    this.authService.logout();
  }

  cancelLogout(): void {
    this.isLogoutConfirmOpen = false;
  }

  getNameLetters(): string {
    const nameComponents = this.authService.getUserFullName()?.split(' ');
    return nameComponents ? nameComponents[0].charAt(0) + nameComponents[nameComponents.length - 1].charAt(0) : '';
  }

  handleBellClick() {
    this.router.navigate(['/pazinojumi']);
  }

  handleNameIconClick() {
    this.router.navigate(['/lietotajs/' + this.userId + '/rediget']);
  }
}
